import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { StaffRail } from '@/components/layout/StaffRail';
import { api } from '@/lib/api';
import { useRealtime, usePolling, fmtMoney, fmtTimeAgo } from '@/lib/realtime';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  LogOut, Navigation, ShieldCheck, Truck, PackageCheck, Phone, MapPin,
  Plus, Pencil, Trash2, BadgeCheck, Building, FileText, ListChecks, History, Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { ORDER_STATUS_LABELS, ORDER_STATUS_COLORS } from '@/lib/orderStatus';
import { ImageUploader } from '@/components/ImageUploader';
import DriverContractDialog from '@/components/DriverContractDialog';

const EMPTY_FORM = {
  full_name: '', phone: '', email: '', password: '',
  city: '', branch_id: '', serves_all_city_branches: true,
  lat: '', lng: '', on_shift: true, id_document_url: '',
};

export default function DriversPage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const isManager = ['admin', 'owner'].includes(user?.role);
  const initialTab = sp.get('tab') || (isManager ? 'active' : 'active');

  return (
    <div className="min-h-screen bg-background">
      <StaffRail />
      <header className="flex items-center justify-between border-b border-border bg-card px-4 py-3 sm:px-6">
        <div>
          <h1 className="font-display text-lg font-semibold">Drivers</h1>
          <p className="text-xs text-muted-foreground">
            Deliveries · {isManager ? 'fleet management' : `${user?.full_name || ''}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isManager && <Button size="sm" variant="secondary" onClick={() => nav('/server')}>Server</Button>}
          <Button data-testid="drivers-logout-button" size="sm" variant="ghost" onClick={() => { logout(); }}>
            <LogOut className="mr-1 h-4 w-4" /> Sign out
          </Button>
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-4 py-6 sm:px-6">
        <Tabs defaultValue={initialTab}>
          <TabsList className="mb-4 flex w-full max-w-2xl flex-wrap gap-1">
            <TabsTrigger data-testid="drivers-tab-active" value="active"><ListChecks className="mr-1 h-3.5 w-3.5" /> Active deliveries</TabsTrigger>
            <TabsTrigger data-testid="drivers-tab-recent" value="recent"><History className="mr-1 h-3.5 w-3.5" /> Recent</TabsTrigger>
            {isManager && (
              <TabsTrigger data-testid="drivers-tab-registry" value="registry"><Users className="mr-1 h-3.5 w-3.5" /> Registered drivers</TabsTrigger>
            )}
          </TabsList>
          <TabsContent value="active"><ActiveDeliveriesPanel me={user} /></TabsContent>
          <TabsContent value="recent"><RecentDeliveriesPanel me={user} /></TabsContent>
          {isManager && (
            <TabsContent value="registry"><RegistryPanel /></TabsContent>
          )}
        </Tabs>
      </main>
    </div>
  );
}

// ---------- Active deliveries ----------
function ActiveDeliveriesPanel({ me }) {
  const [deliveries, setDeliveries] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all'); // all | ready | out_for_delivery
  const [geo, setGeo] = useState({ status: 'idle', lat: null, lng: null, updatedAt: null, error: null });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/delivery/mine');
      setDeliveries(data || []);
    } catch (e) { /* noop */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  useRealtime(['admin:default'], useCallback(() => load(), [load]));
  usePolling(load, 7000);

  // Live-location reporting: only drivers self-report. Skipped for admin/owner
  // viewers so we don't accidentally overwrite their stored coordinates.
  useEffect(() => {
    if (me?.role !== 'driver') return;
    if (!navigator?.geolocation) {
      setGeo({ status: 'unsupported', lat: null, lng: null, updatedAt: null, error: 'Geolocation not supported' });
      return;
    }
    let cancelled = false;
    let lastSent = 0;
    const MIN_INTERVAL_MS = 45 * 1000; // throttle to one ping per ~45s
    const success = async (pos) => {
      if (cancelled) return;
      const lat = pos.coords.latitude;
      const lng = pos.coords.longitude;
      const accuracy = pos.coords.accuracy;
      setGeo({ status: 'live', lat, lng, accuracyM: accuracy, updatedAt: new Date().toISOString(), error: null });
      const now = Date.now();
      if (now - lastSent < MIN_INTERVAL_MS) return;
      lastSent = now;
      try { await api.post('/delivery/me/location', { lat, lng, accuracy_m: accuracy }); } catch (e) { /* ignore transient */ }
    };
    const fail = (err) => {
      if (cancelled) return;
      const code = err?.code;
      const msg = code === 1 ? 'Permission denied — enable location for live dispatch' : code === 2 ? 'Position unavailable' : code === 3 ? 'Location request timed out' : 'Could not read location';
      setGeo({ status: 'error', lat: null, lng: null, updatedAt: null, error: msg });
    };
    const watchId = navigator.geolocation.watchPosition(success, fail, {
      enableHighAccuracy: true,
      maximumAge: 30 * 1000,
      timeout: 15 * 1000,
    });
    return () => { cancelled = true; navigator.geolocation.clearWatch(watchId); };
  }, [me?.role]);

  const visible = useMemo(() => {
    if (statusFilter === 'all') return deliveries;
    return deliveries.filter((d) => d.status === statusFilter);
  }, [deliveries, statusFilter]);

  return (
    <div className="space-y-3">
      {me?.role === 'driver' && (
        <Card data-testid="drivers-location-card">
          <CardContent className="flex flex-wrap items-center justify-between gap-2 p-3 text-sm">
            <div className="flex items-center gap-2">
              <span className={`inline-flex h-2.5 w-2.5 rounded-full ${geo.status === 'live' ? 'bg-emerald-500 animate-pulse' : geo.status === 'error' ? 'bg-rose-500' : geo.status === 'unsupported' ? 'bg-zinc-400' : 'bg-amber-400'}`} />
              <div>
                <p className="font-medium">
                  {geo.status === 'live' ? 'Sharing live location' : geo.status === 'error' ? 'Location off' : geo.status === 'unsupported' ? 'Location unsupported' : 'Acquiring location…'}
                </p>
                <p className="text-[11px] text-muted-foreground">
                  {geo.status === 'live' && geo.lat != null ? `${Number(geo.lat).toFixed(4)}, ${Number(geo.lng).toFixed(4)}${geo.accuracyM ? ` · ±${Math.round(geo.accuracyM)}m` : ''}` : geo.error || 'Used for proximity-based dispatch'}
                </p>
              </div>
            </div>
            {geo.status === 'error' && (
              <span className="text-[11px] text-muted-foreground">Reload the page after enabling location permissions.</span>
            )}
          </CardContent>
        </Card>
      )}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <CardTitle className="flex items-center gap-2 text-base"><Truck className="h-5 w-5 text-primary" /> Active deliveries</CardTitle>
            <Badge variant="secondary" data-testid="drivers-active-count">{deliveries.length}</Badge>
          </div>
          <div className="inline-flex overflow-hidden rounded-md border border-border">
            {[
              { k: 'all', label: 'All' },
              { k: 'ready', label: 'Ready' },
              { k: 'out_for_delivery', label: 'Out for delivery' },
            ].map((opt) => (
              <button
                key={opt.k}
                data-testid={`drivers-active-filter-${opt.k}`}
                onClick={() => setStatusFilter(opt.k)}
                className={`px-3 py-1.5 text-xs font-medium transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary ${statusFilter === opt.k ? 'bg-primary text-primary-foreground' : 'bg-background text-foreground hover:bg-muted'}`}
              >{opt.label}</button>
            ))}
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {!visible.length && (
            <p className="py-6 text-center text-sm text-muted-foreground">No deliveries match the filter right now.</p>
          )}
          {visible.map((o) => (
            <DeliveryCard key={o.id} order={o} me={me} onChange={load} />
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function DeliveryCard({ order, me, onChange }) {
  const [code, setCode] = useState('');
  const [open, setOpen] = useState(false);
  const mine = order.driver_id === me?.id;
  const isDriver = me?.role === 'driver';
  const claim = async () => { try { await api.post(`/delivery/${order.id}/claim`); toast.success('Claimed'); onChange(); } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } };
  const pickup = async () => { try { await api.post(`/delivery/${order.id}/pickup`); toast.success('Picked up, customer notified'); onChange(); } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } };
  const confirm = async () => {
    try {
      await api.post(`/delivery/${order.id}/confirm`, null, { params: { code } });
      toast.success('Delivered!');
      setOpen(false); onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Wrong code'); }
  };
  const navigateMap = () => {
    const addr = order.delivery_address || {};
    const q = encodeURIComponent([addr.line1, addr.line2, addr.city, addr.postal_code].filter(Boolean).join(', '));
    window.open(`https://maps.google.com/?q=${q}`, '_blank');
  };
  return (
    <Card data-testid="drivers-delivery-card" className="border-l-4 border-l-primary">
      <CardContent className="p-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="flex items-center gap-2">
              <span className="font-display text-lg font-semibold">#{order.order_number}</span>
              <Badge className={`border ${ORDER_STATUS_COLORS[order.status]}`}>{ORDER_STATUS_LABELS[order.status]}</Badge>
            </div>
            <p className="text-sm font-medium">{order.customer_name}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              <MapPin className="mr-1 inline h-3 w-3" />
              {order.delivery_address ? [order.delivery_address.line1, order.delivery_address.city].filter(Boolean).join(', ') : 'No address'}
            </p>
            {order.customer_phone && <p className="text-xs text-muted-foreground"><Phone className="mr-1 inline h-3 w-3" /> {order.customer_phone}</p>}
            <ul className="mt-2 text-xs text-muted-foreground">
              {(order.items || []).map((it, idx) => (<li key={idx}>{it.quantity}× {it.name}</li>))}
            </ul>
          </div>
          <div className="text-right">
            <p className="text-lg font-semibold tabular">{fmtMoney(order.total)}</p>
            <p className="text-xs text-muted-foreground">{fmtTimeAgo(order.created_at)}</p>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {isDriver && !order.driver_id && order.status === 'ready' && (
            <Button data-testid="drivers-claim-button" size="sm" onClick={claim}><Truck className="mr-1 h-4 w-4" /> Claim</Button>
          )}
          {isDriver && mine && order.status === 'ready' && <Button data-testid="drivers-pickup-button" size="sm" onClick={pickup}>Picked up</Button>}
          {isDriver && mine && order.status === 'out_for_delivery' && (
            <Dialog open={open} onOpenChange={setOpen}>
              <Button data-testid="drivers-confirm-button" size="sm" onClick={() => setOpen(true)}><PackageCheck className="mr-1 h-4 w-4" /> Confirm delivery</Button>
              <DialogContent>
                <DialogHeader><DialogTitle>Confirm delivery</DialogTitle><DialogDescription>Ask the customer for their 4-digit code to complete the delivery.</DialogDescription></DialogHeader>
                <Input data-testid="drivers-code-input" autoFocus value={code} onChange={(e) => setCode(e.target.value)} placeholder="4-digit code" className="text-2xl tracking-widest text-center" maxLength={4} />
                <DialogFooter><Button data-testid="drivers-code-submit-button" onClick={confirm} disabled={code.length !== 4}><ShieldCheck className="mr-1 h-4 w-4" /> Confirm</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          <Button size="sm" variant="secondary" onClick={navigateMap}><Navigation className="mr-1 h-4 w-4" /> Navigate</Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------- Recent ----------
function RecentDeliveriesPanel({ me }) {
  const [history, setHistory] = useState([]);
  const load = useCallback(async () => {
    try { const { data } = await api.get('/delivery/history'); setHistory(data || []); } catch (e) { /* noop */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <Card>
      <CardHeader><CardTitle className="text-base">Recent deliveries</CardTitle></CardHeader>
      <CardContent>
        <ul className="divide-y divide-border">
          {history.map((o) => (
            <li key={o.id} data-testid="drivers-history-row" className="flex items-center justify-between py-2 text-sm">
              <span><span className="font-semibold">#{o.order_number}</span> · {o.customer_name} · {fmtTimeAgo(o.updated_at)}</span>
              <span className="tabular text-muted-foreground">{fmtMoney(o.total)}</span>
            </li>
          ))}
          {!history.length && <li className="py-4 text-center text-xs text-muted-foreground">No deliveries completed yet.</li>}
        </ul>
      </CardContent>
    </Card>
  );
}

// ---------- Registered drivers (admin/owner only) ----------
function RegistryPanel() {
  const [drivers, setDrivers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showContract, setShowContract] = useState(false);
  const [acceptedContract, setAcceptedContract] = useState(null); // { version }
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    try {
      const [d, b] = await Promise.all([
        api.get('/delivery/drivers', { params: { include_disabled: true } }),
        api.get('/branches'),
      ]);
      setDrivers(d.data || []);
      setBranches(b.data || []);
    } catch (e) {
      toast.error('Failed to load drivers');
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Derive unique cities from branches for the city dropdown — drivers
  // typically work in a city we already operate in.
  const cityOptions = useMemo(() => {
    const set = new Set();
    for (const b of branches) {
      if (b.city) set.add(b.city);
    }
    return Array.from(set).sort();
  }, [branches]);

  const branchesInCity = useMemo(
    () => branches.filter((b) => !form.city || b.city === form.city),
    [branches, form.city],
  );

  const resetAdd = () => {
    setForm(EMPTY_FORM);
    setAcceptedContract(null);
  };

  const create = async () => {
    if (!form.full_name || !form.phone) { toast.error('Name and phone required'); return; }
    if (!form.city) { toast.error('City is required'); return; }
    if (!acceptedContract) { toast.error('Driver must accept the contract first'); return; }
    try {
      const payload = {
        full_name: form.full_name,
        phone: form.phone,
        email: form.email || null,
        password: form.password || null,
        branch_id: form.branch_id || null,
        city: form.city,
        serves_all_city_branches: !!form.serves_all_city_branches,
        lat: form.lat ? Number(form.lat) : null,
        lng: form.lng ? Number(form.lng) : null,
        on_shift: !!form.on_shift,
        id_document_url: form.id_document_url || null,
        contract_version: acceptedContract.version,
        contract_accepted: true,
      };
      await api.post('/delivery/drivers', payload);
      toast.success('Driver added');
      setShowAdd(false);
      resetAdd();
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const startEdit = (d) => {
    setEditId(d.id);
    setEditForm({
      full_name: d.full_name || '', phone: d.phone || '', email: d.email || '',
      password: '', branch_id: d.branch_id || '',
      city: d.city || '', serves_all_city_branches: d.serves_all_city_branches !== false,
      lat: d.lat ?? '', lng: d.lng ?? '', on_shift: !!d.on_shift,
      id_document_url: d.id_document_url || '',
    });
  };
  const saveEdit = async () => {
    try {
      const payload = {
        full_name: editForm.full_name,
        phone: editForm.phone,
        email: editForm.email || null,
        branch_id: editForm.branch_id || null,
        city: editForm.city || null,
        serves_all_city_branches: !!editForm.serves_all_city_branches,
        lat: editForm.lat !== '' ? Number(editForm.lat) : null,
        lng: editForm.lng !== '' ? Number(editForm.lng) : null,
        on_shift: !!editForm.on_shift,
        id_document_url: editForm.id_document_url || null,
      };
      await api.patch(`/delivery/drivers/${editId}`, payload);
      toast.success('Driver updated');
      setEditId(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const toggleShift = async (d) => {
    try {
      await api.patch(`/delivery/drivers/${d.id}`, { on_shift: !d.on_shift });
      load();
    } catch (e) { toast.error('Failed'); }
  };
  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/delivery/drivers/${confirmDelete.id}`);
      toast.success('Driver disabled');
      setConfirmDelete(null);
      load();
    } catch (e) { toast.error('Failed'); }
  };

  return (
    <div className="space-y-3">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Users className="h-5 w-5 text-primary" /> Fleet ({drivers.filter((d) => !d.disabled).length} active / {drivers.length} total)
            </CardTitle>
          </div>
          <Button data-testid="drivers-registry-add-button" size="sm" onClick={() => setShowAdd(true)}>
            <Plus className="mr-1 h-4 w-4" /> Add driver
          </Button>
        </CardHeader>
        <CardContent>
          {!drivers.length ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No drivers yet. Add your first one.</p>
          ) : (
            <ul className="divide-y divide-border">
              {drivers.map((d) => {
                const br = branches.find((b) => b.id === d.branch_id);
                return (
                  <li key={d.id} data-testid="drivers-registry-row" className="flex flex-wrap items-center gap-3 py-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{d.full_name}</p>
                        {d.disabled && <Badge variant="secondary">Disabled</Badge>}
                        {!d.disabled && d.on_shift && <Badge className="border border-emerald-200 bg-emerald-50 text-emerald-900"><BadgeCheck className="mr-1 h-3 w-3" /> On shift</Badge>}
                        {!d.disabled && !d.on_shift && <Badge variant="secondary">Off shift</Badge>}
                        {d.id_document_url && <Badge className="border border-sky-200 bg-sky-50 text-sky-900"><FileText className="mr-1 h-3 w-3" /> ID on file</Badge>}
                      </div>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        <Phone className="mr-1 inline h-3 w-3" /> {d.phone || '—'}
                        {d.city && <> · <MapPin className="mx-1 inline h-3 w-3" /> {d.city}{d.serves_all_city_branches ? ' (all branches)' : ''}</>}
                        {br && <> · <Building className="mx-1 inline h-3 w-3" /> {br.name}</>}
                        {d.lat != null && d.lng != null && <> · {Number(d.lat).toFixed(3)}, {Number(d.lng).toFixed(3)}</>}
                      </p>
                      {d.contract_accepted_at && (
                        <p className="mt-0.5 text-[11px] text-muted-foreground">Contract v{d.contract_version} accepted {fmtTimeAgo(d.contract_accepted_at)}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {!d.disabled && (
                        <Button data-testid="drivers-toggle-shift-button" size="sm" variant="secondary" onClick={() => toggleShift(d)}>
                          {d.on_shift ? 'End shift' : 'Start shift'}
                        </Button>
                      )}
                      <Button data-testid="drivers-edit-button" size="icon" variant="ghost" onClick={() => startEdit(d)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      {!d.disabled && (
                        <Button data-testid="drivers-delete-button" size="icon" variant="ghost" className="text-destructive" onClick={() => setConfirmDelete(d)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Add driver dialog */}
      <Dialog open={showAdd} onOpenChange={(o) => { setShowAdd(o); if (!o) resetAdd(); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>New driver</DialogTitle>
            <DialogDescription>Add a driver to your dispatch pool. A driver belongs to <strong>one city only</strong> — delete the existing record first if they have relocated.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Full name</Label><Input data-testid="drivers-add-name-input" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Phone</Label><Input data-testid="drivers-add-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+15555550100" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Email (optional)</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="driver@example.com" /></div>
              <div className="space-y-1.5"><Label>Password (optional)</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="set for login" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>City</Label>
                <Input
                  data-testid="drivers-add-city-input"
                  list="drivers-add-city-options"
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value, branch_id: '' })}
                  placeholder="e.g. Douala"
                />
                <datalist id="drivers-add-city-options">
                  {cityOptions.map((c) => <option key={c} value={c} />)}
                </datalist>
                <p className="text-[10px] text-muted-foreground">Must match a branch city for dispatch.</p>
              </div>
              <div className="space-y-1.5">
                <Label>Home branch (optional)</Label>
                <select data-testid="drivers-add-branch-select" className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm" value={form.branch_id} onChange={(e) => setForm({ ...form, branch_id: e.target.value })}>
                  <option value="">No specific branch</option>
                  {branchesInCity.map((b) => (<option key={b.id} value={b.id}>{b.name}</option>))}
                </select>
              </div>
            </div>
            <div className="flex items-center gap-2 rounded-md border border-border bg-card p-2 text-sm">
              <Switch data-testid="drivers-add-all-branches-switch" checked={form.serves_all_city_branches} onCheckedChange={(v) => setForm({ ...form, serves_all_city_branches: v })} />
              <Label className="!mt-0">Serves <strong>all branches</strong> in this city (driver moves around)</Label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Latitude (optional)</Label><Input value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} placeholder="e.g. 4.0511" /></div>
              <div className="space-y-1.5"><Label>Longitude (optional)</Label><Input value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} placeholder="e.g. 9.7679" /></div>
            </div>
            <div className="space-y-1.5">
              <Label>Identification document (ID / driver license)</Label>
              <ImageUploader
                value={form.id_document_url}
                onChange={(v) => setForm({ ...form, id_document_url: v })}
                kind="driver_id"
                allowPdf
                placeholder="Upload ID / license (image or PDF)"
                testIdPrefix="drivers-add-id"
              />
            </div>
            <div className="flex items-center gap-2 rounded-md border border-border bg-card p-2 text-sm">
              <Switch checked={form.on_shift} onCheckedChange={(v) => setForm({ ...form, on_shift: v })} />
              <Label className="!mt-0">On shift now</Label>
            </div>
            <div className={`flex items-center gap-2 rounded-md border p-2 text-sm ${acceptedContract ? 'border-emerald-300 bg-emerald-50' : 'border-amber-300 bg-amber-50'}`}>
              {acceptedContract ? (
                <>
                  <BadgeCheck className="h-4 w-4 text-emerald-700" />
                  <span className="text-emerald-900">Contract v{acceptedContract.version} accepted. Ready to add driver.</span>
                </>
              ) : (
                <>
                  <FileText className="h-4 w-4 text-amber-800" />
                  <span className="text-amber-900">Driver must read & accept the contract before registration.</span>
                </>
              )}
              <Button
                data-testid="drivers-add-open-contract"
                size="sm"
                variant={acceptedContract ? 'secondary' : 'default'}
                className="ml-auto"
                onClick={() => setShowContract(true)}
              >
                {acceptedContract ? 'Re-open contract' : 'Open contract'}
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button
              data-testid="drivers-add-submit-button"
              onClick={create}
              disabled={!acceptedContract}
              title={!acceptedContract ? 'Driver must accept the contract first' : undefined}
            >
              Add driver
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DriverContractDialog
        open={showContract}
        onOpenChange={setShowContract}
        driverName={form.full_name || undefined}
        onAccept={({ version }) => { setAcceptedContract({ version }); toast.success(`Contract v${version} accepted`); }}
      />

      {/* Edit driver dialog */}
      <Dialog open={!!editId} onOpenChange={(o) => { if (!o) setEditId(null); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Edit driver</DialogTitle><DialogDescription>Update contact, city, branch and location.</DialogDescription></DialogHeader>
          <div className="grid gap-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Full name</Label><Input value={editForm.full_name} onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Phone</Label><Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Email</Label><Input value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>City</Label>
                <Input
                  list="drivers-edit-city-options"
                  value={editForm.city}
                  onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                />
                <datalist id="drivers-edit-city-options">
                  {cityOptions.map((c) => <option key={c} value={c} />)}
                </datalist>
              </div>
              <div className="space-y-1.5">
                <Label>Home branch</Label>
                <select className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm" value={editForm.branch_id} onChange={(e) => setEditForm({ ...editForm, branch_id: e.target.value })}>
                  <option value="">No specific branch</option>
                  {branches.filter((b) => !editForm.city || b.city === editForm.city).map((b) => (<option key={b.id} value={b.id}>{b.name}</option>))}
                </select>
              </div>
            </div>
            <div className="flex items-center gap-2 rounded-md border border-border bg-card p-2 text-sm">
              <Switch checked={!!editForm.serves_all_city_branches} onCheckedChange={(v) => setEditForm({ ...editForm, serves_all_city_branches: v })} />
              <Label className="!mt-0">Serves all branches in this city</Label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Latitude</Label><Input value={editForm.lat} onChange={(e) => setEditForm({ ...editForm, lat: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Longitude</Label><Input value={editForm.lng} onChange={(e) => setEditForm({ ...editForm, lng: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5">
              <Label>Identification document</Label>
              <ImageUploader
                value={editForm.id_document_url}
                onChange={(v) => setEditForm({ ...editForm, id_document_url: v })}
                kind="driver_id"
                allowPdf
                placeholder="Replace or upload ID / license"
                testIdPrefix="drivers-edit-id"
              />
            </div>
            <div className="flex items-center gap-2 rounded-md border border-border bg-card p-2 text-sm">
              <Switch checked={!!editForm.on_shift} onCheckedChange={(v) => setEditForm({ ...editForm, on_shift: v })} />
              <Label className="!mt-0">On shift</Label>
            </div>
          </div>
          <DialogFooter><Button data-testid="drivers-edit-save-button" onClick={saveEdit}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => { if (!o) setConfirmDelete(null); }}>
        <AlertDialogContent data-testid="drivers-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Disable {confirmDelete?.full_name}?</AlertDialogTitle>
            <AlertDialogDescription>
              They'll be removed from active dispatch and can no longer accept new deliveries. Past orders remain linked. You can re-register them in a different city after this.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="drivers-delete-confirm-button" onClick={doDelete}>Disable driver</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
